import * as sdk from "matrix-js-sdk";
import PmsUrlConfig from '../urlConfig.js';
import { safeSetInterval } from '../utils/intervalManager.js';
import {  
    getMatrixServer, 
    getJoinedRoomsForUser, 
    getRoomNotificationCounts,
    getUserGroups
} from '../components/matrix/matrixFuntions.js';
import { LOGIN_ERROR_MESSAGES, MATRIX_NOTIFICATION_INTERVAL } from '../constants/loginConstants.js';
import { saveUser } from '../utils/storageUtils.js';
import store from '../redux/store.js';

const matrixAuthenticationPromises = new Map();
const matrixAuthenticationRetryAfter = new Map();

const authenticateMatrixClientInternal = async (loggedInUser, dispatch) => {
    try {
        console.log('=== MATRIX AUTHENTICATION START ===');
        console.log('User Type:', loggedInUser.user_type);
        console.log('🔍 Checking synapse credentials in loggedInUser:', {
            hasSynapseUserId: !!loggedInUser.synapse_user_id,
            synapseUserId: loggedInUser.synapse_user_id,
            hasSynapsePassword: !!loggedInUser.password,
            hasSynapseToken: !!loggedInUser.synapse_token,
        });

        const synapseUserId = loggedInUser.synapse_user_id;
        const synapsePassword = loggedInUser.password;
        const matrixToken = loggedInUser.synapse_token;

        if (!synapseUserId || (!synapsePassword && !matrixToken)) {
            console.warn('⚠️ Missing synapse credentials:', {
                synapseUserId: !!synapseUserId,
                synapsePassword: !!synapsePassword,
                matrixToken: !!matrixToken
            });
            
            dispatch({
                type: 'matrixAuthError',
                payload: LOGIN_ERROR_MESSAGES.MATRIX_MISSING_WARNING
            });
            return;
        }

        const matrixClient = await createAndAuthenticateMatrixClient(synapseUserId, synapsePassword, matrixToken);
        await setupMatrixClient(matrixClient, synapseUserId, matrixToken, dispatch);
        return matrixClient;
        
    } catch (synapseError) {
        console.error('❌ Synapse credentials verification failed:', synapseError);
        console.error('Error details:', {
            message: synapseError.message,
            status: synapseError.httpStatus,
            data: synapseError.data
        });
        throw synapseError;
    }
};

export const authenticateMatrixClient = (loggedInUser, dispatch) => {
    const synapseUserId = loggedInUser?.synapse_user_id;
    if (!synapseUserId) {
        return authenticateMatrixClientInternal(loggedInUser, dispatch);
    }

    const retryAfter = matrixAuthenticationRetryAfter.get(synapseUserId);
    if (retryAfter && retryAfter > Date.now()) {
        const remainingSeconds = Math.ceil((retryAfter - Date.now()) / 1000);
        const rateLimitError = new Error(`Matrix login is rate limited. Try again in ${remainingSeconds} seconds.`);
        rateLimitError.httpStatus = 429;
        return Promise.reject(rateLimitError);
    }
    matrixAuthenticationRetryAfter.delete(synapseUserId);

    const existingAttempt = matrixAuthenticationPromises.get(synapseUserId);
    if (existingAttempt) {
        return existingAttempt;
    }

    const attempt = authenticateMatrixClientInternal(loggedInUser, dispatch).catch((error) => {
        if (error.httpStatus === 429 || error.data?.errcode === 'M_LIMIT_EXCEEDED') {
            const retryAfterMs = Number(error.data?.retry_after_ms);
            const cooldownMs = Number.isFinite(retryAfterMs) && retryAfterMs > 0
                ? retryAfterMs
                : 60000;
            matrixAuthenticationRetryAfter.set(synapseUserId, Date.now() + cooldownMs);
        }
        throw error;
    });
    let trackedAttempt;
    trackedAttempt = attempt.finally(() => {
        if (matrixAuthenticationPromises.get(synapseUserId) === trackedAttempt) {
            matrixAuthenticationPromises.delete(synapseUserId);
        }
    });
    matrixAuthenticationPromises.set(synapseUserId, trackedAttempt);
    return trackedAttempt;
};

const createAndAuthenticateMatrixClient = async (synapseUserId, synapsePassword, matrixToken) => {
    console.log('=== MATRIX AUTHENTICATION DEBUG ===');
    console.log('Synapse User ID:', synapseUserId);
    console.log('Has Synapse Password:', !!synapsePassword);
    console.log('Has Matrix Token:', !!matrixToken);
    console.log('Password length:', synapsePassword?.length || 0);
    console.log('Token length:', matrixToken?.length || 0);
    console.log('=== END MATRIX AUTHENTICATION DEBUG ===');
    
    let matrixClient;
    let loginResponse;

    if (synapsePassword) {
        console.log('🔑 Using synapse password for authentication');
        console.log('🔍 Matrix Login Details:');
        console.log('  - Homeserver:', PmsUrlConfig.MATRIX_HOME_SERVER_URL);
        console.log('  - User ID:', synapseUserId);
        console.log('  - Password length:', synapsePassword.length);
        
        matrixClient = sdk.createClient({
            baseUrl: PmsUrlConfig.MATRIX_HOME_SERVER_URL
        });
        
        try {
            loginResponse = await matrixClient.login("m.login.password", {
                user: synapseUserId,
                password: synapsePassword
            });
            
            console.log('🔐 Login successful:', loginResponse);
            matrixClient.setAccessToken(loginResponse.access_token);
        } catch (loginError) {
            if (loginError.httpStatus === 403) {
                console.log('⚠️ User does not exist, attempting to register...');
                
                try {
                    const registerResponse = await matrixClient.registerRequest({
                        username: synapseUserId.split(':')[0].replace('@', ''),
                        password: synapsePassword,
                        auth: { type: "m.login.dummy" }
                    });
                    
                    console.log('✅ User registered successfully:', registerResponse);
                    
                    loginResponse = await matrixClient.login("m.login.password", {
                        user: synapseUserId,
                        password: synapsePassword
                    });
                    
                    console.log('🔐 Login after registration successful:', loginResponse);
                    matrixClient.setAccessToken(loginResponse.access_token);
                } catch (registerError) {
                    console.error('❌ User registration failed:', registerError);
                    throw new Error(`Matrix user registration failed: ${registerError.message}`);
                }
            } else {
                throw loginError;
            }
        }
    } else if (matrixToken) {
        console.log('Using matrix token for authentication');
        console.log('Matrix Token Details:');
        console.log('  - Homeserver:', PmsUrlConfig.MATRIX_HOME_SERVER_URL);
        console.log('  - User ID:', synapseUserId);
        console.log('  - Token length:', matrixToken.length);
        
        matrixClient = sdk.createClient({
            baseUrl: PmsUrlConfig.MATRIX_HOME_SERVER_URL,
            accessToken: matrixToken,
            userId: synapseUserId
        });
        
        console.log('Matrix client created with token authentication');
    } else {
        throw new Error('Neither synapse password nor matrix token provided');
    }

    const testResponse = await matrixClient.whoami();
    console.log('Synapse credentials verified:', testResponse);
    console.log('User ID:', testResponse.user_id);
    console.log('Homeserver:', testResponse.home_server);

    return matrixClient;
};

const setupMatrixClient = async (matrixClient, synapseUserId, matrixToken, dispatch) => {
    const matrixServer = getMatrixServer(matrixToken, synapseUserId);
    
    getJoinedRoomsForUser(matrixClient, dispatch);
    await matrixClient.startClient({ initialSyncLimit: 30000, lazyLoadMembers: true });
    
    matrixClient.once("sync", function (state, prevState, res) {
        if (state === "PREPARED") {
            console.log("Matrix client prepared successfully");
            
            // Update the user object in Redux state with the Matrix access token
            const loginDetails = store.getState().loginDetails;
            if (loginDetails && loginDetails.loggedInUser) {
                const updatedUser = {
                    ...loginDetails.loggedInUser,
                    access_token: matrixClient.getAccessToken()
                };
                
                dispatch({
                    type: 'setLoggedInUser',
                    payload: updatedUser
                });
                
                // Also update localStorage
                saveUser(updatedUser);
                
                console.log("✅ Matrix access token updated in user state");
                
                // Get user groups and rooms after successful login
                setTimeout(async () => {
                    try {
                        console.log("🔍 === GETTING USER GROUPS AND ROOMS AFTER LOGIN ===");
                        const membershipInfo = await getUserGroups(matrixClient, updatedUser.synapse_user_id);
                        console.log("📊 User membership info after login:", membershipInfo);
                        
                        // Store membership info in Redux for easy access
                        dispatch({
                            type: 'setUserMembership',
                            payload: membershipInfo
                        });
                    } catch (error) {
                        console.error("❌ Error getting user groups after login:", error);
                    }
                }, 1000); // Wait 1 second for sync to complete
            }
        } else {
            console.error("Matrix client sync not prepared: ", state);
        }
    });
    
    console.log('=== STORING MATRIX CLIENT IN REDUX ===');
    console.log('Matrix client access token:', matrixClient.getAccessToken());
    console.log('Matrix client user ID:', matrixClient.getUserId());
    console.log('=== END STORING MATRIX CLIENT ===');
    
    dispatch({
        type: 'setMatrixClient',
        payload: matrixClient
    });
    
    dispatch({
        type: 'setMatrixServer',
        payload: matrixServer
    });

    getRoomNotificationCounts(matrixClient, dispatch);
    safeSetInterval(getRoomNotificationCounts, MATRIX_NOTIFICATION_INTERVAL, 'Matrix Notifications Check', matrixClient, dispatch);
};
